import { useState } from 'react';
import {
  HOUR, LOST_REASONS, addDays, at, calc, dateOf, conflicts, fDT, fShort, inr, renderMessage, suggestTemplates, toLocal, whatsappLink,
  type BookingRecord,
} from '@nandanam/domain';
import { useStore, type ActResult } from '../app/store';
import { messageData, messageFile, quoteFile, sharePicture } from '../app/quoteImage';
import { Chips, Field, Modal, MoneyInput } from '../ui/components';
import { Icon } from '../ui/icons';
import { BAR } from '../app/selectors';
import {
  CheckedLine, ErrorBox, ModeChips, PricingEditor, Success, ViewOnlyNote, conflictText, patchFrom, previewWith, pricingDraft,
  pricingProblem, timeNow, todayISO, availabilityFor, useSubmit,
} from './kit';

export function HoldOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id), s = S.state.settings;
  const [hours, setHours] = useState(s.holdHours);
  const [res, setRes] = useState<ActResult | null>(null);
  const sub = useSubmit();
  if (!r) return null;
  const av = availabilityFor(S, r);
  const exp = toLocal(S.now + hours * HOUR);
  const save = () => sub.run(async () => { const x = await S.act('record.placeHold', `record:${id}`, { id, hours }); if (x.ok) setRes(x); return x; });
  if (res) return (
    <Modal kicker="Hold" title="Dates on hold" onClose={() => S.close()}>
      <Success title={`Held until ${fDT(r.hold?.expires ?? exp)}`} body={`${S.cust(r.cust).name} · ${fShort(r.items[0]!.start, r.items[r.items.length - 1]!.end)}. Released automatically if not confirmed.`}
        checked={<CheckedLine res={res} />}
        actions={[{ label: 'WhatsApp hold confirmation', go: () => S.open('message', { id, tpl: 'hold' }) }, { label: 'Done', primary: true, go: () => S.close() }]} />
    </Modal>
  );
  return (
    <Modal kicker="Hold" title="Place hold" sub={`${S.num(r)} · ${S.cust(r.cust).name}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!av.ok || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Checking and saving…' : 'Place hold'}</button></>}>
      <ViewOnlyNote />
      {av.box}
      <Field label="Hold for"><select className="select" value={hours} onChange={e => setHours(Number(e.target.value))}>{s.holdOptions.map(h => <option key={h} value={h}>{h} hours{h === s.holdHours ? ' (default)' : ''}</option>)}</select></Field>
      <div style={{ fontSize: 14 }}>Expires <b>{fDT(exp)}</b>. The dates are blocked until then and release automatically.</div>
      <div className="muted" style={{ fontSize: 12 }}>Needs a connection: the app checks OneDrive for other devices' holds before and after saving.</div>
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export function ConfirmOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id);
  const [d, setD] = useState(() => (r ? pricingDraft(r) : null));
  const [amount, setAmount] = useState<number | ''>('');
  const [ref, setRef] = useState('');
  const [mode, setMode] = useState(S.state.settings.payModes[0]!);
  const [advId] = useState(() => S.newId());
  const [res, setRes] = useState<ActResult | null>(null);
  const sub = useSubmit();
  if (!r || !d) return null;
  const av = availabilityFor(S, r);
  const patch = patchFrom(r, d, S), k = calc(previewWith(r, patch));
  const advHint = r.terms.advanceMode === 'percent' ? `${r.terms.advancePct}% = ${inr(k.advanceDue)}` : `usually ${inr(k.advanceDue)}`;
  const problem = !av.ok ? 'Dates not available' : pricingProblem(r, d) ?? (k.total <= 0 ? 'Total must be more than ₹0' : Number(amount || 0) > k.total ? 'Advance is more than the booking total' : null);
  const save = () => sub.run(async () => {
    const x = await S.act('record.confirm', `record:${id}`, { id, pricing: patch, advance: Number(amount) > 0 ? { id: advId, amount, mode, ref: ref.trim() } : null });
    if (x.ok) setRes(x);
    return x;
  });
  if (res) return (
    <Modal kicker="Booking" title="Booking confirmed" onClose={() => S.close()}>
      <Success title={`${S.cust(r.cust).name} is booked`} body={`${inr(k.total)} total · ${Number(amount) > 0 ? `${inr(Number(amount))} advance received` : 'no advance recorded yet'}`}
        checked={<CheckedLine res={res} />}
        actions={[{ label: 'Print pro-forma bill', go: () => S.nav('bill', { id }) }, { label: 'WhatsApp booking confirmation', go: () => S.open('message', { id, tpl: 'confirmation' }) }, { label: 'Open record', primary: true, go: () => S.nav('booking', { id }) }]} />
    </Modal>
  );
  return (
    <Modal size="lg" kicker="Booking" title="Confirm booking" sub={`${S.num(r)} · ${S.cust(r.cust).name}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!!problem || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Checking and saving…' : 'Confirm booking'}</button></>}>
      <ViewOnlyNote />
      {av.box}
      <PricingEditor r={r} d={d} set={setD} />
      <div className="grid2" style={{ paddingTop: 6, borderTop: '1px solid var(--line-3)' }}>
        <Field label={`Advance received · ${advHint}`}><MoneyInput value={amount} onChange={setAmount} /></Field>
        <Field label="Reference"><input className="input" placeholder="Optional" value={ref} onChange={e => setRef(e.target.value)} /></Field>
      </div>
      {Number(amount) > 0 && <Field label="Mode"><ModeChips value={mode} onChange={setMode} /></Field>}
      {amount !== '' && Number(amount) < k.advanceDue && <div className="warn-box">Below the {inr(k.advanceDue)} advance usually required. You can still confirm.</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export function PricingOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id);
  const [d, setD] = useState(() => (r ? pricingDraft(r) : null));
  const sub = useSubmit();
  if (!r || !d) return null;
  const patch = patchFrom(r, d, S), k = calc(previewWith(r, patch)), paid = calc(r).paid;
  const problem = pricingProblem(r, d) ?? (k.total <= 0 ? 'Total must be more than ₹0' : k.total < paid ? `Total cannot be below the ${inr(paid)} already received` : null);
  const save = () => sub.run(async () => { const x = await S.act('record.adjustPricing', `record:${id}`, { id, patch }, 'Pricing updated'); if (x.ok) S.close(); return x; });
  return (
    <Modal size="lg" kicker="Pricing" title="Adjust pricing" sub={`${S.num(r)} · ${S.cust(r.cust).name} · standard prices are kept for history`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!!problem || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Save pricing'}</button></>}>
      <ViewOnlyNote />
      <PricingEditor r={r} d={d} set={setD} />
      {problem && <div className="error-box">{problem}</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export function LostOverlay({ id, release }: { id: string; release?: boolean }) {
  const S = useStore(), r = S.rec(id);
  const [reason, setReason] = useState(release ? 'Plans changed' : LOST_REASONS[0]!);
  const [detail, setDetail] = useState('');
  const sub = useSubmit();
  if (!r) return null;
  const full = reason === 'Other' ? detail.trim() : detail.trim() ? `${reason} — ${detail.trim()}` : reason;
  const save = () => sub.run(async () => {
    const x = release ? await S.act('record.releaseHold', `record:${id}`, { id, reason: full }, 'Hold released — dates are free') : await S.act('record.markLost', `record:${id}`, { id, reason: full }, 'Marked as lost');
    if (x.ok) S.close();
    return x;
  });
  return (
    <Modal kicker={release ? 'Hold' : 'Enquiry'} title={release ? 'Release hold' : 'Mark as lost'} sub={`${S.num(r)} · ${S.cust(r.cust).name}${release ? ' · the dates become free again' : ''}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn danger" disabled={!full || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : release ? 'Release hold' : 'Mark as lost'}</button></>}>
      <Field label="Reason"><Chips value={reason} options={LOST_REASONS.map(x => ({ v: x, label: x }))} onChange={setReason} /></Field>
      <Field label={reason === 'Other' ? 'Detail (required)' : 'Detail (optional)'}><input className="input" value={detail} onChange={e => setDetail(e.target.value)} placeholder="e.g. Booked a hall in town" /></Field>
      <div className="muted" style={{ fontSize: 12 }}>The record is kept and can be reopened as an enquiry later.</div>
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

/** WhatsApp: copy or open with the text. Either action is logged as what it is — not as proof of sending. */
export function MessageOverlay({ id, tpl: initial }: { id: string; tpl?: string }) {
  const S = useStore(), r = S.rec(id), s = S.state.settings;
  const sug = r ? suggestTemplates(r, S.now) : [];
  const [key, setKey] = useState(initial ?? sug[0] ?? 'quotation');
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState<string | null>(null);
  const [pic, setPic] = useState<{ file: File; url: string; for: string } | null>(null);
  const [making, setMaking] = useState(false);
  const [full, setFull] = useState(false);
  if (!r) return null;
  const c = S.cust(r.cust);
  // Only messages that print the booking number wait for it to settle.
  const prov = /\{id\}/.test(s.templates[key]?.text ?? '') && (S.isProvisional(r) || (!r.number && !S.view.numbers.get(r.id)));
  const text = edited ?? renderMessage(key, { ...r, number: S.num(r) }, c, s);
  const to = c.whatsapp || c.phone;
  const keys = [...sug, ...Object.keys(s.templates).filter(k => !sug.includes(k))];
  const choose = (k: string) => { setKey(k); setEdited(null); setEditing(false); };
  const log = (action: 'copied' | 'opened' | 'picture') => S.act('record.logComm', `record:${id}`, { id, key, action, commId: S.newId() }).then(x => { if (!x.ok) S.toast(`${action === 'copied' ? 'Copied' : 'Opened'} — but not logged on the record: ${x.error}`); });
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { S.toast('Could not copy — select the text and copy it yourself'); return; }
    S.toast('Message copied'); void log('copied');
  };
  const open = () => { window.open(whatsappLink(to, text), '_blank', 'noopener'); void log('opened'); };
  // Every message can also go as a picture, through the phone's share sheet (pick WhatsApp there).
  // The quotation has its own layout; the others show the message text in the same style.
  const isQuote = key === 'quotation';
  const picFor = `${key}\n${text}`, current = pic?.for === picFor ? pic : null;
  const makePicture = async () => {
    if (current) return current.file;
    const today = dateOf(S.now), label = s.templates[key]?.label ?? 'Message';
    const file = isQuote && edited === null ? await quoteFile(r, c, s, S.num(r), today) : await messageFile(messageData(key, label, text, c, s, S.num(r), today), S.num(r));
    if (pic) URL.revokeObjectURL(pic.url);
    setPic({ file, url: URL.createObjectURL(file), for: picFor });
    return file;
  };
  const sendPicture = async () => {
    setMaking(true);
    try {
      const file = await makePicture();
      const how = await sharePicture(file);
      if (how === 'saved') S.toast('Picture saved. Attach it in WhatsApp.');
      if (how !== 'cancelled') void log('picture');
    } catch (e) { S.toast(e instanceof Error ? e.message : String(e)); }
    finally { setMaking(false); }
  };
  const preview = async () => { setMaking(true); try { await makePicture(); setFull(true); } catch (e) { S.toast(e instanceof Error ? e.message : String(e)); } finally { setMaking(false); } };
  return (
    <Modal size="xl" kicker="WhatsApp" title={s.templates[key]?.label ?? 'Message'} sub={`To ${c.name} · ${to}`} onClose={() => S.close()}
      footer={<>
        <span className="muted" style={{ fontSize: 12, marginRight: 'auto', alignSelf: 'center' }}>Opening WhatsApp does not prove the message was sent.</span>
        <button className="btn" disabled={prov} onClick={copy}><Icon name="copy" size={15} />Copy text</button>
        <button className="btn" disabled={prov || !to} onClick={open}><Icon name="send" size={15} />Send as text</button>
        <button className="btn primary" disabled={prov || making} onClick={sendPicture}><Icon name="send" size={15} />{making ? 'Preparing…' : 'Send as picture'}</button>
      </>}>
      {full && current && (
        // The whole picture on one screen, as the customer will see it. Tap anywhere to close.
        <div role="dialog" aria-label="Picture preview" onClick={() => setFull(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,30,30,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 12 }}>
          <img src={current.url} alt="Message picture, full screen" style={{ maxWidth: '100%', maxHeight: 'calc(100dvh - 60px)', objectFit: 'contain', borderRadius: 10 }} />
          <span style={{ color: '#F5F5DC', fontSize: 13 }}>Tap anywhere to close</span>
        </div>
      )}
      <div className="msg-layout">
        <div className="msg-list">
          {keys.map(k => (
            <button key={k} className={k === key ? 'on' : ''} onClick={() => choose(k)}>
              <span>{s.templates[k]?.label ?? k}</span>{sug.includes(k) && <span className="kicker-sm">Suggested</span>}
            </button>
          ))}
        </div>
        <div style={{ flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 13 }}>Filled from the booking — edits apply to this message only.</span>
            <button className="btn small ghost" onClick={() => { setEditing(!editing); if (!editing) setEdited(text); }}>{editing ? 'Preview' : 'Edit text'}</button>
          </div>
          {!editing && (current
            ? <img src={current.url} alt="Message picture" onClick={() => setFull(true)} style={{ display: 'block', width: 'auto', maxWidth: '100%', maxHeight: '55vh', margin: '0 auto', borderRadius: 12, border: '1px solid var(--btn-line)', cursor: 'zoom-in' }} />
            : <button className="btn small" disabled={making || prov} onClick={preview}>See the picture</button>)}
          {editing
            ? <textarea className="textarea" style={{ minHeight: 330, lineHeight: 1.55 }} value={text} onChange={e => setEdited(e.target.value)} />
            : <div className="bubble">{text}</div>}
          {prov && <div className="warn-box">Booking number {S.num(r)} is still provisional — it is confirmed a few minutes after saving, once no other device could have taken it. Messages wait until then so a guest never gets a number that changes.</div>}
        </div>
      </div>
    </Modal>
  );
}

/** Owner blocks: personal use, maintenance… Cannot overlap holds or bookings. */
export function BlockOverlay({ date }: { date?: string }) {
  const S = useStore(), s = S.state.settings;
  const d0 = date ?? addDays(todayISO(S), 1);
  const [type, setType] = useState(s.blockTypes[0]!);
  const [from, setFrom] = useState(d0), [fromT, setFromT] = useState('08:00');
  const [to, setTo] = useState(d0), [toT, setToT] = useState('20:00');
  const [note, setNote] = useState('');
  const [bid] = useState(() => S.newId());
  const sub = useSubmit();
  const start = at(from, fromT), end = at(to, toT);
  const bad = !from || !to || end <= start;
  const cs = bad ? [] : conflicts({ records: S.state.records, blocks: [] }, start, end, { now: S.now, bufferHours: 0 });
  const overBlocks = bad ? [] : S.state.blocks.filter(b => start < b.end && end > b.start);
  const save = () => sub.run(async () => { const x = await S.act('block.add', `block:${bid}`, { id: bid, type, start, end, note: note.trim() }, 'Dates blocked'); if (x.ok) S.close(); return x; });
  const bar = /Maint|Repair/.test(type) ? BAR.maint : BAR.block;
  return (
    <Modal kicker="Calendar" title="Block dates" sub="For personal use or maintenance. No gap is added around blocks." onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={bad || cs.length > 0 || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Block dates'}</button></>}>
      <ViewOnlyNote />
      <Field label="Type"><Chips value={type} options={s.blockTypes.map(t => ({ v: t, label: t }))} onChange={setType} /></Field>
      <div className="block-grid">
        <Field label="From"><input type="date" className="input" value={from} onChange={e => { setFrom(e.target.value); if (e.target.value > to) setTo(e.target.value); }} /></Field>
        <Field label="Time"><input type="time" className="input" value={fromT} onChange={e => setFromT(e.target.value)} /></Field>
        <Field label="Until"><input type="date" className="input" value={to} min={from} onChange={e => setTo(e.target.value)} /></Field>
        <Field label="Time"><input type="time" className="input" value={toT} onChange={e => setToT(e.target.value)} /></Field>
      </div>
      <Field label="Note"><input className="input" placeholder="e.g. Pool pump service" value={note} onChange={e => setNote(e.target.value)} /></Field>
      {bad ? <div className="error-box">The end must be after the start.</div>
        : cs.length ? <div className="error-box"><b>Cannot block.</b> Overlaps {cs.map(c => conflictText(S, c)).join('; ')}. Blocks cannot overlap holds or bookings.</div>
        : <div className="good-box" style={{ background: 'transparent', border: `1px solid ${bar.bc}`, color: bar.fg }}><Icon name={bar.icon} size={14} /> {type} · {fShort(start, end)}{overBlocks.length ? ` · overlaps ${overBlocks.length} existing block${overBlocks.length > 1 ? 's' : ''}` : ''}</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export { timeNow };
export type { BookingRecord };
